import React, { useRef } from 'react';

import { RecurrencyRole, TenantUserPermission } from '@recurrency/core-api-schema/dist/common/enums';
import { TenantSettingKey, TenantSettingsFields } from '@recurrency/core-api-schema/dist/common/tenantSettings';
import { SearchPlanningDTO } from '@recurrency/core-api-schema/dist/search/getSearchPlanning';
import { SearchIndexName } from '@recurrency/core-api-schema/dist/searchIndex/common';

import { getPlanningColumns, getPlanningValueFacets } from 'pages/planning/utils';

import { LastUpdatedMomentForecast } from 'components/recipes/LastUpdatedMoment';
import { SearchFrame } from 'components/recipes/SearchFrame';
import { SearchFrameContext } from 'components/recipes/SearchFrame/types';
import { InfoTooltip } from 'components/Tooltip/InfoTooltip';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { showAsyncModal } from 'utils/asyncModal';
import { truthy } from 'utils/boolean';
import { arePermissionsEnabled, hasUserPermission } from 'utils/roleAndTenant';
import { useHashState } from 'utils/routes';
import { ViewSettingKey } from 'utils/tableAndSidePaneSettings/types';
import { asKeyOf } from 'utils/tables';

import { SearchFrameHashState } from 'types/hash-state';

import { ReplenishmentMethod, usePlanningFilterBootstrap } from '../utils';
import { SparseStockGridModal } from './modals/SparseStockGridModal';
import { PlanningSavedFilters } from './PlanningSavedFilters';
import { PlanningSearchFrameHeader } from './PlanningSearchFrameHeader';
import { PlanningSidePane } from './PlanningSidePane';

export const PlanningPage = () => {
  const { activeTenant, activeUser } = useGlobalApp();
  const [hashState] = useHashState<SearchFrameHashState>();
  const searchFrameContextRef = useRef<SearchFrameContext<SearchPlanningDTO>>();
  usePlanningFilterBootstrap();

  const showOpOqVariant = !!hashState.where?.recurrency_replenishment_method?.includes(ReplenishmentMethod.OPOQ);

  const sparseItemGridRoles = [
    RecurrencyRole.TenantAdmin,
    ...(TenantSettingsFields[TenantSettingKey.CalculationSparseMonthsStockExtended].roles || []),
  ];

  // call the latest ref of searchIndexReload
  const searchIndexReload = () => searchFrameContextRef.current?.searchIndexReload();
  const clearSelection = () => searchFrameContextRef.current?.clearSelection();

  const columns = getPlanningColumns({
    activeTenant,
    activeUser,
    isOpOqVariant: showOpOqVariant,
    searchIndexReload,
    openSparseItemGrid: (
      arePermissionsEnabled()
        ? !hasUserPermission(activeTenant, TenantUserPermission.AccessSettingsGeneralView)
        : !activeTenant.tenantUser.recurrencyRoles.some((role) => sparseItemGridRoles.includes(role.name))
    )
      ? () => showAsyncModal(SparseStockGridModal, {})
      : undefined,
  }).filter(truthy);

  const valueFacets = getPlanningValueFacets({
    activeTenant,
    activeUser,
  });

  // There is a secondary sort on the search endpoint, and we want to use it if
  //  they are either using the default sort or sorting by predictability tag
  const useSecondarySort =
    !showOpOqVariant &&
    (!hashState.sortBy?.field || hashState.sortBy?.field === asKeyOf<SearchPlanningDTO>('predictability_tag_sort'));

  return (
    <SearchFrame<SearchPlanningDTO>
      title={<InfoTooltip title="Used for setting mins, maxes, lead times, and safety stock">Planning</InfoTooltip>}
      subtitle={<LastUpdatedMomentForecast includeForecastPrefix />}
      indexName={SearchIndexName.Planning}
      queryPlaceholder="Search by item"
      columns={columns}
      defaultSortBy={{
        field: asKeyOf<SearchPlanningDTO>('predictability_tag_sort'),
        order: 'desc',
      }}
      secondarySortBy={
        useSecondarySort
          ? { field: asKeyOf<SearchPlanningDTO>('projected_inventory_change_abs'), order: 'desc' }
          : undefined
      }
      valueFacets={valueFacets.filter(truthy)}
      sidePane={({ record }) => <PlanningSidePane record={record} searchIndexReload={searchIndexReload} />}
      // The setting below is inversed but should have no side effects. Do not change it because user's local storage will cause them to lose their preferences.
      tableColumnsSettingKey={showOpOqVariant ? ViewSettingKey.PlanningTable : ViewSettingKey.PlanningTableOPOQ}
      tableRowSelection="selectAll"
      contextRef={searchFrameContextRef}
      savedFiltersToolbar={<PlanningSavedFilters clearSelection={clearSelection} />}
      headerActions={(searchFrameContext) => (
        <PlanningSearchFrameHeader
          valueFacets={valueFacets}
          showOpOqVariant={showOpOqVariant}
          searchFrameContext={searchFrameContext}
          searchIndexReload={searchIndexReload}
          clearSelection={clearSelection}
        />
      )}
    />
  );
};
